import { Box } from "@mui/material";
import { FavoriteRounded } from "@mui/icons-material";
import { useApp, useTheme } from "contexts";
import { User } from "assets/images";
import { Common } from "components";
import { EnumTheme } from "utils/enums";

export const Parallax = () => {
  const { t } = useApp();
  const { theme } = useTheme();
  const isDark = theme.id === EnumTheme.Dark;

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: { xs: "stretch", md: "flex-end" },
        position: "relative",
      }}
    >
      {/* Main Character / Operator Dossier Frame */}
      <Box
        sx={{
          width: "100%",
          maxWidth: { xs: "100%", md: 340 },
          backgroundColor: theme.color.theme.color,
          border: `2px solid ${isDark ? theme.color.border.accent : "#191b22"}`,
          borderRadius: theme.border.square,
          p: { xs: theme.spacing.sm, sm: theme.spacing.md, md: theme.spacing.sm },
          boxShadow: `4px 4px 0px 0px ${isDark ? "#000000" : "#191b22"}`,
          position: "relative",
          transition: "transform 0.15s ease, box-shadow 0.15s ease",
          "&:hover": {
            transform: "translate(-2px, -2px)",
            boxShadow: `6px 6px 0px 0px ${isDark ? "#000000" : "#191b22"}`,
          },
        }}
      >
        {/* Top Retro Telemetry Strip */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pb: theme.spacing.xs,
            mb: theme.spacing.xs,
            borderBottom: `1px solid ${theme.color.border.color}`,
            fontFamily: '"Space Mono", monospace',
            fontSize: theme.font.xxxs,
            letterSpacing: "0.1em",
            color: theme.color.font.accent,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              color: theme.color.primary.color,
              fontWeight: theme.font.bold,
            }}
          >
            <Box
              component="span"
              sx={{
                width: 8,
                height: 8,
                backgroundColor: theme.color.primary.color,
                display: "inline-block",
                boxShadow: `0 0 6px ${theme.color.primary.color}`,
              }}
            />
            {t.parallax.player_status}
          </Box>
          <Box component="span">{t.parallax.slot}</Box>
        </Box>

        {/* Responsive Inner Layout: Horizontal Dossier on Tablet (sm), Vertical Trading Card on xs & md+ */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row", md: "column" },
            alignItems: { xs: "stretch", sm: "stretch", md: "stretch" },
            gap: { xs: theme.spacing.xs, sm: theme.spacing.md, md: theme.spacing.xs },
          }}
        >
          {/* Authentic User Portrait Container */}
          <Box
            sx={{
              position: "relative",
              overflow: "hidden",
              backgroundColor: theme.color.background.accent,
              border: `1px solid ${theme.color.border.color}`,
              borderRadius: theme.border.square,
              aspectRatio: { xs: "16 / 11", sm: "4 / 5", md: "4 / 5" },
              width: { xs: "100%", sm: 200, md: "100%" },
              flexShrink: 0,
            }}
          >
            <Box
              component="img"
              alt={t.parallax.img_alt}
              src={User}
              width={340}
              height={425}
              {...({ fetchPriority: "high" } as any)}
              decoding="async"
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                objectPosition: "center top",
                filter: "grayscale(10%) contrast(1.05)",
                transition: "transform 0.3s ease",
                "&:hover": {
                  transform: "scale(1.04)",
                },
              }}
            />
            {/* Subtle Pixel Scanline Overlay */}
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                pointerEvents: "none",
                opacity: 0.25,
                backgroundImage: `radial-gradient(${theme.color.border.color} 1px, transparent 1px)`,
                backgroundSize: "8px 8px",
              }}
            />
            {/* Reticle Corner Accents inside Viewport */}
            <Box
              sx={{
                position: "absolute",
                top: 6,
                left: 6,
                width: 10,
                height: 10,
                borderTop: `2px solid ${theme.color.primary.color}`,
                borderLeft: `2px solid ${theme.color.primary.color}`,
                pointerEvents: "none",
                opacity: 0.85,
              }}
            />
            <Box
              sx={{
                position: "absolute",
                bottom: 6,
                right: 6,
                width: 10,
                height: 10,
                borderBottom: `2px solid ${theme.color.primary.color}`,
                borderRight: `2px solid ${theme.color.primary.color}`,
                pointerEvents: "none",
                opacity: 0.85,
              }}
            />
          </Box>

          {/* Operator Metadata & Social Deck */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              gap: theme.spacing.xs,
            }}
          >
          
            {/* Role Badge & Health Hearts */}
            <Box
              sx={{
                pt: theme.spacing.xxs,
                borderTop: {
                  xs: `1px solid ${theme.color.border.color}`,
                  sm: "none",
                  md: `1px solid ${theme.color.border.color}`,
                },
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                fontSize: theme.font.xxs,
              }}
            >
              <Box
                sx={{
                  fontFamily: '"Space Mono", monospace',
                  fontSize: theme.font.xxxs,
                  fontWeight: theme.font.bold,
                  color: theme.color.font.color,
                  letterSpacing: "0.08em",
                }}
              >
                {t.parallax.role_badge}
              </Box>
              <Box
                sx={{
                  display: "flex",
                  gap: theme.spacing.xxs,
                  color: theme.color.status.error.color,
                }}
              >
                <FavoriteRounded sx={{ fontSize: theme.font.xs }} />
                <FavoriteRounded sx={{ fontSize: theme.font.xs }} />
                <FavoriteRounded sx={{ fontSize: theme.font.xs }} />
              </Box>
            </Box>

            {/* Integrated Social Bar inside Tablet Dossier for zero wasted space */}
            {/* <Box
              sx={{
                display: { xs: "none", sm: "flex", md: "none" },
                pt: theme.spacing.md,
                borderTop: `1px dashed ${theme.color.border.color}`,
                justifyContent: "flex-start",
              }}
            >
              <Common.Social size="small" />
            </Box> */}
          </Box>
        </Box>

        {/* Outer Frame Corner Notches */}
        <Box
          sx={{
            position: "absolute",
            top: -3,
            right: -3,
            width: 6,
            height: 6,
            backgroundColor: isDark ? "#b1c5ff" : "#191b22",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: -3,
            left: -3,
            width: 6,
            height: 6,
            backgroundColor: isDark ? "#b1c5ff" : "#191b22",
          }}
        />
      </Box>

      {/* Social Bar Attached Beneath Card on Mobile (xs) and Desktop (md+) */}
      {/* <Box
        sx={{
          mt: theme.spacing.xl,
          display: { xs: "flex", sm: "none", md: "flex" },
          justifyContent: "center",
          width: "100%",
          maxWidth: { xs: "100%", md: 340 },
        }}
      >
        <Common.Social size="small" />
      </Box> */}
    </Box>
  );
};